import { Button, TextField } from '@mui/material'
import { type ChangeHandler, useForm } from 'react-hook-form'

import { Box, Modal } from '@/components/ui'
import {
  INTAKE_FORM_NAME_MAX_LENGTH,
  type IntakeFormListItem,
} from '@/definitions/intakeForm'
import { useSnackbar } from '@/hooks/useSnackbar'

import { useReplaceCustomForm } from '../../hooks/useReplaceCustomForm'
import { DefinitionField } from '../DefinitionField'
import {
  NAME_REQUIRED_MESSAGE,
  NAME_TOO_LONG_MESSAGE,
} from '../intakeFormEditorCopy'
import { IntakeFormVerdictAlerts } from '../IntakeFormEditorParts'

const DEFINITION_HELPER_TEXT = 'The JSON produced by the SurveyJS designer.'

interface ReplaceIntakeFormValues {
  display_name: string
  form_template_json: string
}

interface ReplaceIntakeFormModalProps {
  form: IntakeFormListItem
  onClose: () => void
  orgId: string
}

const savedMessage = (displayName: string, enabled: boolean) => {
  if (enabled) {
    return `${displayName} was replaced. The link already handed out now opens these questions.`
  }
  return (
    `${displayName} was replaced. It stays off, so that link still says the form ` +
    'was not found until someone switches it back on.'
  )
}

/**
 * Replaces a custom form's questions, keeping the link already handed out.
 *
 * The checks are the same as a first upload: a definition that cannot work is
 * refused, and one that only carries caveats takes a second press. A form that
 * is switched off stays off — replacing the questions does not turn it back on.
 */
export const ReplaceIntakeFormModal = ({
  orgId,
  form,
  onClose,
}: ReplaceIntakeFormModalProps) => {
  const { showSuccess } = useSnackbar()

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ReplaceIntakeFormValues>({
    defaultValues: {
      display_name: form.display_name,
      form_template_json: '',
    },
  })

  const replacement = useReplaceCustomForm(orgId, form.base_form_id, {
    onNameRefused: (message) => setError('display_name', { message }),
    onSaved: (saved) => {
      showSuccess(savedMessage(saved.display_name, saved.enabled))
      onClose()
    },
  })

  const withRetiredRefusal =
    (onChange: ChangeHandler): ChangeHandler =>
      (event) => {
        replacement.retireRefusal()
        return onChange(event)
      }

  const nameField = register('display_name', {
    validate: (value) => value.trim() !== '' || NAME_REQUIRED_MESSAGE,
    maxLength: { value: INTAKE_FORM_NAME_MAX_LENGTH, message: NAME_TOO_LONG_MESSAGE },
  })
  const definitionField = register('form_template_json')

  const onSubmit = (values: ReplaceIntakeFormValues) =>
    replacement.submit({
      displayName: values.display_name,
      formTemplateJson: values.form_template_json,
      formVersion: form.form_version,
    })

  return (
    <Modal
      title='Replace intake form'
      onClose={onClose}
      disableClose={replacement.isSaving}
      maxWidth='md'
      actions={
        <>
          <Button onClick={onClose} disabled={replacement.isSaving}>
            Cancel
          </Button>
          <Button
            type='submit'
            form='replace-intake-form'
            variant='contained'
            disabled={replacement.isSaving}
          >
            {replacement.isAwaitingConfirmation ? 'Save anyway' : 'Save'}
          </Button>
        </>
      }
    >
      <Box
        component='form'
        id='replace-intake-form'
        variant='flexColumn'
        gap='md'
        onSubmit={handleSubmit(onSubmit)}
      >
        <TextField
          {...nameField}
          onChange={withRetiredRefusal(nameField.onChange)}
          label='Display name'
          fullWidth
          error={Boolean(errors.display_name)}
          helperText={errors.display_name?.message}
          disabled={replacement.isSaving}
        />

        <DefinitionField
          {...definitionField}
          onChange={withRetiredRefusal(definitionField.onChange)}
          label='Form definition'
          helperText={DEFINITION_HELPER_TEXT}
          multiline
          minRows={14}
          maxRows={22}
          fullWidth
          disabled={replacement.isSaving}
        />

        <IntakeFormVerdictAlerts
          isAwaitingConfirmation={replacement.isAwaitingConfirmation}
          plainRefusalKeepsDefinitionHeading={false}
          problem={replacement.problem}
          refusal={replacement.refusal}
          warnings={replacement.warnings}
        />
      </Box>
    </Modal>
  )
}
